import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { SITE, scrollToId } from "@/config/site";
import { PlayButton } from "./PlayButton";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(href);
  };

  return (
    <header
      data-testid="nav-header"
      className={`fixed inset-x-0 top-0 z-40 transition-all duration-300 ${
        scrolled || open ? "border-b border-slate-200/70 bg-white/85 backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:h-20 sm:px-6 lg:px-8">
        <a
          href="#home"
          onClick={(e) => go(e, "#home")}
          data-testid="nav-logo"
          aria-label="DENSA — back to top"
          className="flex items-center"
        >
          <img src={SITE.assets.logoPrimary} alt="DENSA — Dental Excellence & Networked Smart Administration" className="h-8 w-auto sm:h-10" />
        </a>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {SITE.nav.map((item) => (
            <a
              key={item.href}
              href={item.href}
              onClick={(e) => go(e, item.href)}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/[^a-z]/g, "")}`}
              className="text-sm font-medium text-slate-600 transition-colors duration-200 hover:text-brand"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:block" data-testid="nav-download-cta">
            <PlayButton testid="nav-download-btn" className="min-h-[44px] py-2 pl-4 pr-5 shadow-md" />
          </div>
          <button
            type="button"
            data-testid="nav-mobile-hamburger"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full text-brand-ink transition-colors hover:bg-brand-mint lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-slate-100 bg-white/95 backdrop-blur-xl lg:hidden"
            aria-label="Mobile"
            data-testid="nav-mobile-menu"
          >
            <div className="space-y-1 px-4 py-4">
              {SITE.nav.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => go(e, item.href)}
                  data-testid={`nav-mobile-link-${item.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                  className="block rounded-xl px-4 py-3 text-base font-medium text-slate-700 transition-colors hover:bg-brand-mint hover:text-brand"
                >
                  {item.label}
                </a>
              ))}
              <div className="px-4 pt-3">
                <PlayButton testid="nav-mobile-download-btn" className="w-full justify-center" />
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
